import React, { useMemo } from 'react';
import { GameState, League } from '../../domain/entities';
import { View } from '../../App';
import { getReadableAccentColor } from '../../utils/utilities';

export const LinkedText: React.FC<{
    text: string;
    gameState: GameState;
    pushView: (view: View) => void;
    onClose: () => void;
    as?: React.ElementType;
    style?: React.CSSProperties;
    className?: string;
}> = ({ text, gameState, pushView, onClose, as: Component = 'p', style, className }) => {
    const entities = useMemo(() => {
        const teams = Object.values(gameState.leagues).flatMap((l: League) => (l.teams || []).map(t => ({ name: t.name, type: 'team' as const })));
        const players = Object.values(gameState.leagues).flatMap((l: League) => (l.teams || []).flatMap(t => (t.squad || []).map(p => ({ name: p.name, type: 'player' as const, id: p.id }))));
        // Sort by length descending to match longer names first (e.g., "West Bromwich" before "West")
        return [...teams, ...players].sort((a, b) => b.name.length - a.name.length);
    }, [gameState]);

    const handleEntityClick = (name: string, type: 'player' | 'team', id?: number) => {
        onClose(); // Close the modal first
        if (type === 'team') {
            pushView({ type: 'teamDetail', title: `Team: ${name}`, teamName: name });
        } else if (type === 'player' && id) {
            pushView({ type: 'playerDetail', title: `Player: ${name}`, playerId: id });
        }
    };

    const parts = useMemo(() => {
        if (entities.length === 0) return [text];
        const regex = new RegExp(`(${entities.map(e => e.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'g');
        return text.split(regex).filter(part => part);
    }, [text, entities]);
    
    return (
        <Component style={style} className={className}>
            {parts.map((part, index) => {
                const entity = entities.find(e => e.name === part);
                if (entity) {
                    // Get team colors for hover styling
                    let hoverColor = 'var(--accent-text-color)';
                    if (entity.type === 'team') {
                        const team = Object.values(gameState.leagues)
                            .flatMap(l => l.teams || [])
                            .find(t => t.name === entity.name);
                        if (team?.colors) {
                            const bgColor = getComputedStyle(document.documentElement)
                                .getPropertyValue('--secondary-color')
                                .trim();
                            hoverColor = getReadableAccentColor(team.colors.primary, bgColor, 4.5);
                        }
                    } else if (entity.type === 'player') {
                        // For players, use their team's color
                        const player = Object.values(gameState.leagues)
                            .flatMap(l => l.teams || [])
                            .flatMap(t => t.squad || [])
                            .find(p => p.id === (entity as any).id);
                        if (player) {
                            const playerTeam = Object.values(gameState.leagues)
                                .flatMap(l => l.teams || [])
                                .find(t => t.squad?.some(p => p.id === player.id));
                            if (playerTeam?.colors) {
                                const bgColor = getComputedStyle(document.documentElement)
                                    .getPropertyValue('--secondary-color')
                                    .trim();
                                hoverColor = getReadableAccentColor(playerTeam.colors.primary, bgColor, 4.5);
                            }
                        }
                    }

                    return (
                        <button
                            key={index}
                            className="link-style"
                            onClick={() => handleEntityClick(entity.name, entity.type, (entity as any).id)}
                            style={{
                                '--link-hover-color': hoverColor
                            } as React.CSSProperties}
                        >
                            {part}
                        </button>
                    );
                }
                return <span key={index}>{part}</span>;
            })}
        </Component>
    );
};